import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { X, Mail, CheckCircle, AlertTriangle } from 'lucide-react';
import { useDialogA11y } from '../ui/useDialogA11y';

interface UserModalProps {
  isOpen:    boolean;
  onClose:   () => void;
  onSuccess: () => void;
  userId?:   string;
}

function autoGenPassword(nombre: string, apellido: string): string {
  const n = nombre.trim()[0]?.toLowerCase() ?? '';
  const a = apellido.trim()[0]?.toLowerCase() ?? '';
  return n && a ? `${n}${a}12345` : '';
}

export function UserModal({ isOpen, onClose, onSuccess, userId }: UserModalProps) {
  const [formData, setFormData] = useState({
    email:     '',
    password:  '',
    nombre:    '',
    apellido:  '',
    full_name: '', // solo para modo edición
    role:      'alumno',
  });
  const [sendEmail, setSendEmail] = useState(true);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [emailStatus, setEmailStatus] = useState<'sent' | 'failed' | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(isOpen, onClose, dialogRef);

  useEffect(() => {
    if (userId) {
      loadUser();
    } else {
      resetForm();
    }
  }, [userId, isOpen]);

  const loadUser = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) throw error;
      if (data) {
        setFormData({ email: data.email, password: '', nombre: '', apellido: '', full_name: data.full_name || '', role: data.role });
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', nombre: '', apellido: '', full_name: '', role: 'alumno' });
    setError('');
    setEmailStatus(null);
    setSendEmail(true);
  };

  const handleNombre = (nombre: string) => {
    const pwd = autoGenPassword(nombre, formData.apellido);
    setFormData(f => ({ ...f, nombre, ...(pwd ? { password: pwd } : {}) }));
  };

  const handleApellido = (apellido: string) => {
    const pwd = autoGenPassword(formData.nombre, apellido);
    setFormData(f => ({ ...f, apellido, ...(pwd ? { password: pwd } : {}) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setEmailStatus(null);

    try {
      if (userId) {
        // Modo edición: solo actualiza perfil, sin email
        const { error } = await supabase
          .from('profiles')
          .update({ full_name: formData.full_name, role: formData.role })
          .eq('id', userId);
        if (error) throw error;
      } else {
        // Modo creación: llama a Edge Function que crea user + envía email
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No hay sesión activa');

        const full_name = `${formData.nombre.trim()} ${formData.apellido.trim()}`.trim();

        const apiUrl  = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-create-user`;
        const ctrl    = new AbortController();
        const timer   = setTimeout(() => ctrl.abort(), 30_000);
        let response: Response;
        try {
          response = await fetch(apiUrl, {
            method:  'POST',
            signal:  ctrl.signal,
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type':  'application/json',
            },
            body: JSON.stringify({
              email:      formData.email,
              password:   formData.password,
              full_name,
              role:       formData.role,
              send_email: sendEmail,
            }),
          });
        } finally {
          clearTimeout(timer);
        }

        let result: any;
        try { result = await response.json(); }
        catch { throw new Error(`Error del servidor (HTTP ${response.status})`); }
        if (!result.success) throw new Error(result.error || 'Error al crear usuario');

        const sent = result.email_sent ?? false;
        if (sendEmail) setEmailStatus(sent ? 'sent' : 'failed');

        await onSuccess();
        setTimeout(() => { onClose(); resetForm(); }, 5000);
        return;
      }

      await onSuccess();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Error saving user:', error);
      setError(error.message || 'Error al guardar el usuario');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-user"
        tabIndex={-1}
        className="glass-surface rounded-xl max-w-md w-full border border-white/[0.06]"
      >
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
          <h2 id="modal-title-user" className="text-xl font-bold text-white">
            {userId ? 'Editar Usuario' : 'Nuevo Usuario'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-white/[0.04] rounded-lg transition-all">
            <X className="w-5 h-5 text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500 rounded-lg text-red-500 text-sm">
              {error}
            </div>
          )}

          {emailStatus === 'sent' && (
            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/40 rounded-lg text-green-400 text-sm">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              Correo de bienvenida enviado correctamente.
            </div>
          )}
          {emailStatus === 'failed' && (
            <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/40 rounded-lg text-amber-400 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              Usuario creado, pero el correo no se pudo enviar. Revisa el RESEND_API_KEY.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Email</label>
            <input
              type="email"
              required
              disabled={!!userId}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Nombre y Apellido solo en creación */}
          {!userId ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Nombre(s)</label>
                <input
                  type="text"
                  required
                  value={formData.nombre}
                  onChange={(e) => handleNombre(e.target.value)}
                  className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Apellido(s)</label>
                <input
                  type="text"
                  required
                  value={formData.apellido}
                  onChange={(e) => handleApellido(e.target.value)}
                  className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Nombre Completo</label>
              <input
                type="text"
                required
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
              />
            </div>
          )}

          {/* Contraseña solo en creación, visible como texto */}
          {!userId && (
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Contraseña</label>
              <input
                type="text"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime font-mono"
                minLength={7}
                placeholder="Se genera automáticamente"
              />
              <p className="text-xs text-subtle mt-1">Generada automáticamente · puedes editarla · mínimo 7 caracteres</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Rol</label>
            <select
              required
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
            >
              <option value="alumno">Alumno</option>
              <option value="docente">Docente</option>
              <option value="admin">Administrador</option>
            </select>
          </div>

          {!userId && (
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  sendEmail
                    ? 'bg-cubus-lime border-cubus-lime'
                    : 'border-white/30 bg-transparent'
                }`}>
                  {sendEmail && (
                    <svg className="w-3 h-3 text-cubus-bg" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/70 group-hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                Enviar correo de bienvenida con credenciales
              </div>
            </label>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg rounded-lg transition-all font-semibold disabled:opacity-50"
            >
              {loading
                ? (userId ? 'Guardando...' : 'Creando...')
                : (userId ? 'Actualizar' : 'Crear Usuario')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
